import { useParams } from 'react-router-dom'
import {
  useCreateInviteMutation,
  useDeleteInviteMutation,
  useFetchChronicleQuery,
  useUpdateCharacterMutation,
} from '../../store'
import { FadeIn } from '../../components/FadeIn'
import { Overlay } from '../../components/Overlay'
import { Row, Col, Button, Modal, Input, InputNumber } from 'antd'
import { CharacterSelect } from './style'
import { useEffect, useState } from 'react'
import { AttributeCard } from '../../components/AttributeCard'
import { ToastContainer } from 'react-toastify'

import {
  GiAngelOutfit,
  GiSnake,
  GiClover,
  GiHeavyHelm,
  GiSkullCrack,
  GiSwapBag,
  GiSwitchWeapon,
  GiHearts,
  GiDove,
  GiDoubleFaceMask,
  GiBleedingEye,
  GiBookshelf,
  GiBlackBook,
  GiCalendar,
  GiEnvelope,
  GiThreeFriends,
} from 'react-icons/gi'
import * as S from './style'

export const AdventureDetailsPage = () => {
  const { id } = useParams()
  const { data: chronicle, refetch, isLoading, isFetching } = useFetchChronicleQuery(id)
  const [updateCharacterData, characterData] = useUpdateCharacterMutation()
  const [createInvite, inviteData] = useCreateInviteMutation()
  const [deleteInvite, deleteInviteData] = useDeleteInviteMutation()
  const [itemDescription, setItemDescription] = useState('')
  const [itemModifier, setItemModifier] = useState('')
  const [character, setCharacter] = useState({} as any)
  const [open, setOpen] = useState(false)
  const [modalTitle, setModalTitle] = useState('')

  useEffect(() => {
    if (characterData.isSuccess) refetch()
  }, [characterData])

  useEffect(() => {
    if (inviteData.isSuccess) refetch()
  }, [inviteData])

  useEffect(() => {
    if (deleteInviteData.isSuccess) refetch()
  }, [deleteInviteData])

  useEffect(() => {
    setCharacter(chronicle?.characters.find((c: any) => c?.id === character?.id))
  }, [chronicle])

  const handleRemoveConditions = (id: string) => {
    const conditions = [...character.conditions]
    const index = conditions.findIndex((condition) => condition.id === id)
    conditions[index] = { ...conditions[index], markedForDeletion: true }

    updateCharacterData({ conditions, id: character.id })
  }

  const handleRemoveMerits = (id: string) => {
    const merits = [...character.merits]
    const index = merits.findIndex((merit) => merit.id === id)
    merits[index] = { ...merits[index], markedForDeletion: true }

    updateCharacterData({ merits, id: character.id })
  }

  const handleRemoveItems = (id: string) => {
    const items = [...character.items]
    const index = items.findIndex((item) => item.id === id)
    items[index] = { ...items[index], markedForDeletion: true }

    updateCharacterData({ items, id: character.id })
  }

  const handleItemDescriptionChange = (event: any) => {
    const { value } = event.target
    setItemDescription(value)
  }

  const handleItemModifierChange = (event: any) => {
    const value = event?.target?.value || event
    setItemModifier(value)
  }

  const addItem = (isWeapon: boolean, isArmor: boolean, description: string, modifiers: string) => {
    const items = [...character.items]
    items.push({
      armor: isArmor,
      weapon: isWeapon,
      description,
      modifiers,
      character_id: character.id,
    })
    return items
  }

  const addMerit = (description: string, level: string) => {
    const merits = [...character.merits]
    merits.push({
      description,
      level,
      character_id: character.id,
    })
    return merits
  }

  const addCondition = (description: string, reason: string) => {
    const conditions = [...character.conditions]
    conditions.push({
      description,
      reason,
      character_id: character.id,
    })
    return conditions
  }

  const handleOk = async () => {
    const charData = { ...character }

    if (modalTitle === 'condition') {
      charData.conditions = addCondition(itemDescription, itemModifier)
    }

    if (modalTitle === 'merit') {
      charData.merits = addMerit(itemDescription, itemModifier)
    }

    if (modalTitle === 'weapon') {
      charData.items = addItem(true, false, itemDescription, itemModifier)
    }

    if (modalTitle === 'armor') {
      charData.items = addItem(false, true, itemDescription, itemModifier)
    }

    if (modalTitle === 'item') {
      charData.items = addItem(false, false, itemDescription, itemModifier)
    }

    updateCharacterData({
      conditions: charData.conditions,
      items: charData.items,
      merits: charData.merits,
    })

    setOpen(false)
    setItemDescription('')
    setItemModifier('')
  }

  const getItemIcon = (isArmor: boolean, isWeapon: boolean) => {
    if (isWeapon) return <GiSwitchWeapon size={48} />
    if (isArmor) return <GiHeavyHelm size={48} />

    return <GiSwapBag size={48} />
  }

  return (
    <Overlay
      isLoading={
        isLoading ||
        chronicle?.isLoading ||
        characterData?.isLoading ||
        inviteData.isLoading ||
        deleteInviteData.isLoading ||
        isFetching
      }
    >
      {chronicle && !isLoading && (
        <FadeIn>
          <>
            <Row justify={'start'} gutter={[16, 0]}>
              <Col span={24}>
                <AttributeCard
                  icon={<GiBookshelf size={48} />}
                  type={'Title'}
                  attribute={chronicle?.name}
                  margin={'8px 0'}
                  stars={false}
                />
              </Col>
              <Col span={24}>
                <AttributeCard
                  icon={<GiBlackBook size={48} />}
                  type={'Summary'}
                  attribute={chronicle?.short_description}
                  margin={'8px 0'}
                  stars={false}
                />
              </Col>
              <Col span={24}>
                <AttributeCard
                  icon={<GiCalendar size={48} />}
                  type={'Open since'}
                  attribute={new Intl.DateTimeFormat('en-US', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  }).format(new Date(chronicle.creation_date))}
                  margin={'8px 0'}
                  stars={false}
                />
              </Col>
              {chronicle?.invites?.map((invite: any) => (
                <Col key={invite.id} xs={24} sm={12} md={6}>
                  <AttributeCard
                    icon={<GiEnvelope size={48} />}
                    type={'Invite (Pending)'}
                    attribute={invite.id}
                    margin={'8px 0'}
                    stars={false}
                    removable
                    editHook={() => deleteInvite(invite.id)}
                  />
                </Col>
              ))}
              <Col xs={24} sm={12} md={6} onClick={() => createInvite(chronicle.id)}>
                <AttributeCard
                  icon={<GiThreeFriends size={48} />}
                  type={'Invite Friends'}
                  attribute={'Click to create invite'}
                  margin={'8px 0'}
                  stars={false}
                />
              </Col>
              <ToastContainer position={'bottom-center'} />
            </Row>
            <Row justify={'center'} gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Row style={{ marginTop: 24 }}>
                  <span style={{ fontFamily: 'HonestMaria', fontSize: 36 }}>Select character</span>
                </Row>
                <CharacterSelect
                  placeholder={'Character name'}
                  options={chronicle?.characters?.map((character: any) => ({
                    label: character.name,
                    value: character.id,
                  }))}
                  onChange={(value: any) => {
                    setCharacter(() =>
                      chronicle?.characters.find((character: any) => character.id === value),
                    )
                  }}
                />
              </Col>
            </Row>
            {character?.id ? (
              <FadeIn>
                <>
                  <Row justify={'center'} gutter={[16, 16]} style={{ marginTop: 24 }}>
                    <Col>
                      <span style={{ fontFamily: 'HonestMaria', fontSize: 36 }}>
                        Character Info
                      </span>
                    </Col>
                  </Row>
                  <Row justify={'start'} gutter={[16, 16]}>
                    <S.SectionDivider> Base stats </S.SectionDivider>
                    <Col xs={24} sm={12} md={6} xxl={4}>
                      <AttributeCard
                        icon={<GiBleedingEye size={48} />}
                        type={'Concept'}
                        attribute={character?.concept}
                        margin={'8px 0'}
                        stars={false}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6} xxl={4}>
                      <AttributeCard
                        icon={<GiDove size={48} />}
                        type={'Willpower'}
                        attribute={character?.current_willpower}
                        levelCap={character?.max_willpower}
                        margin={'8px 0'}
                        stars={false}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6} xxl={4}>
                      <AttributeCard
                        icon={<GiHearts size={48} />}
                        type={'Health'}
                        attribute={character?.current_health}
                        levelCap={character?.max_health}
                        margin={'8px 0'}
                        stars={false}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6} xxl={4}>
                      <AttributeCard
                        icon={<GiAngelOutfit size={48} />}
                        type={'Virtue'}
                        attribute={character?.virtue}
                        margin={'8px 0'}
                        stars={false}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6} xxl={4}>
                      <AttributeCard
                        icon={<GiSnake size={48} />}
                        type={'Vice'}
                        attribute={character?.vice}
                        margin={'8px 0'}
                        stars={false}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6} xxl={4}>
                      <AttributeCard
                        icon={<GiDoubleFaceMask size={48} />}
                        type={'Integrity'}
                        attribute={character?.integrity}
                        margin={'8px 0'}
                        levelCap={'10'}
                        stars={false}
                      />
                    </Col>
                  </Row>
                  <Row justify={'start'} gutter={[16, 16]}>
                    <S.SectionDivider> Merits </S.SectionDivider>
                    {character?.merits?.length ? (
                      character.merits.map((merit: any) => {
                        return (
                          <Col xs={24} sm={12} md={6} xxl={4} key={merit.id}>
                            <AttributeCard
                              icon={<GiClover size={48} />}
                              type={merit.description}
                              attribute={merit.level}
                              margin={'8px 0'}
                              stars={false}
                              removable
                              editHook={() => handleRemoveMerits(merit.id)}
                            />
                          </Col>
                        )
                      })
                    ) : (
                      <S.SectionDivider empty> No Merits </S.SectionDivider>
                    )}
                  </Row>
                  <Row justify={'start'} gutter={[16, 16]}>
                    <S.SectionDivider> Conditions </S.SectionDivider>
                    {character?.conditions?.length ? (
                      character.conditions.map((condition: any) => {
                        return (
                          <Col xs={24} sm={12} md={6} xxl={4} key={condition.id}>
                            <AttributeCard
                              icon={<GiSkullCrack size={48} />}
                              type={condition.description}
                              attribute={condition.reason}
                              margin={'8px 0'}
                              stars={false}
                              removable
                              editHook={() => handleRemoveConditions(condition.id)}
                            />
                          </Col>
                        )
                      })
                    ) : (
                      <S.SectionDivider empty> No Conditions </S.SectionDivider>
                    )}
                  </Row>
                  <Row justify={'start'} gutter={[16, 16]}>
                    <S.SectionDivider> Items </S.SectionDivider>
                    {character?.items?.length ? (
                      character?.items?.map((item: any) => {
                        const icon = getItemIcon(item.armor, item.weapon)
                        return (
                          <Col xs={24} sm={12} md={6} xxl={4} key={item.id}>
                            <AttributeCard
                              icon={icon}
                              type={item.description}
                              attribute={item.modifiers}
                              margin={'8px 0'}
                              stars={false}
                              removable
                              editHook={() => handleRemoveItems(item.id)}
                            />
                          </Col>
                        )
                      })
                    ) : (
                      <S.SectionDivider empty> No Items </S.SectionDivider>
                    )}
                  </Row>
                  <Row
                    justify={'center'}
                    gutter={[16, 16]}
                    style={{ marginTop: 24, marginBottom: 24 }}
                  >
                    <Col>
                      <span style={{ fontFamily: 'HonestMaria', fontSize: 36 }}>Select action</span>
                    </Col>
                  </Row>
                  <Row justify={'center'} gutter={[16, 16]} style={{ paddingBottom: 10 }}>
                    <Col xs={24} sm={12} md={6} xxl={4}>
                      <Button
                        style={{ width: '100%' }}
                        type="primary"
                        /*htmlType="submit"*/ size={'large'}
                        onClick={() => {
                          setModalTitle('item')
                          setOpen(true)
                        }}
                      >
                        Give Item
                      </Button>
                    </Col>
                    <Col xs={24} sm={12} md={6} xxl={4}>
                      <Button
                        style={{ width: '100%' }}
                        type="primary"
                        /*htmlType="submit"*/ size={'large'}
                        onClick={() => {
                          setModalTitle('weapon')
                          setOpen(true)
                        }}
                      >
                        Give Weapon
                      </Button>
                    </Col>
                    <Col xs={24} sm={12} md={6} xxl={4}>
                      <Button
                        style={{ width: '100%' }}
                        type="primary"
                        /*htmlType="submit"*/ size={'large'}
                        onClick={() => {
                          setModalTitle('armor')
                          setOpen(true)
                        }}
                      >
                        Give Armor
                      </Button>
                    </Col>
                    <Col xs={24} sm={12} md={6} xxl={4}>
                      <Button
                        style={{ width: '100%' }}
                        type="primary"
                        /*htmlType="submit"*/ size={'large'}
                        onClick={() => {
                          setModalTitle('merit')
                          setOpen(true)
                        }}
                      >
                        Give Merit
                      </Button>
                    </Col>
                    <Col xs={24} sm={12} md={6} xxl={4}>
                      <Button
                        style={{ width: '100%' }}
                        type="primary"
                        /*htmlType="submit"*/ size={'large'}
                        onClick={() => {
                          setModalTitle('condition')
                          setOpen(true)
                        }}
                      >
                        Give Condition
                      </Button>
                    </Col>
                  </Row>
                  <Modal
                    title={`Give ${modalTitle}:`}
                    open={open}
                    onOk={handleOk}
                    onCancel={() => setOpen(false)}
                  >
                    <label>Description</label>
                    <Input
                      onChange={handleItemDescriptionChange}
                      value={itemDescription}
                      placeholder={'Description'}
                    />
                    <label>
                      {modalTitle === 'condition'
                        ? 'Reason'
                        : modalTitle === 'merit'
                        ? 'Level'
                        : 'Modifiers'}
                    </label>
                    {modalTitle !== 'merit' ? (
                      <Input
                        onChange={handleItemModifierChange}
                        value={itemModifier}
                        placeholder={modalTitle === 'condition' ? 'Reason' : 'Modifiers'}
                      />
                    ) : (
                      <InputNumber
                        onChange={handleItemModifierChange}
                        placeholder={'Level'}
                        value={itemModifier}
                        style={{ width: '100%' }}
                      />
                    )}
                  </Modal>
                </>
              </FadeIn>
            ) : (
              <></>
            )}
          </>
        </FadeIn>
      )}
    </Overlay>
  )
}
